import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { LoadMoreButton } from "@/components/site/LoadMoreButton";

import { Link, createFileRoute } from "@tanstack/react-router";

import { DateBlock } from "@/components/site/DateBlock";
import inductionSeminar from "@/assets/induction-seminar.webp.asset.json";
import { meetingsQuery } from "@/lib/content";
import { meetingDateParts, meetingLocationLabel } from "@/lib/meeting-format";
import { useCompleteRows } from "@/lib/use-complete-rows";
import { useAuth0, useMemberLogin } from "@/integrations/fc/auth0";

const FALLBACK_IMAGE = inductionSeminar.url;

// 12 fills complete rows at every breakpoint of the seminar grid (1/2/3/4 columns).
const GUEST_LIMIT = 12;
const GRID_BREAKPOINTS = [[640, 2], [1024, 3], [1225, 4]] as const;


const STATS = [
  { value: "29", label: "induction seminars held in 2.5 years" },
  { value: "1021", label: "diplomats have participated to date" },
];

const FIELDS = [
  { label: "Title (H.E. / Prof / Dr)", required: false, type: "text" },
  { label: "First name", required: true, type: "text" },
  { label: "Last name", required: true, type: "text" },
  { label: "Email (Embassy)", required: true, type: "email" },
  { label: "Country", required: true, type: "text" },
];

export const Route = createFileRoute("/new-diplomats")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): { more?: 1 } =>
    search["more"] === 1 || search["more"] === "1" ? { more: 1 } : {},
  loader: ({ context }) => context.queryClient.ensureQueryData(meetingsQuery()),
  head: () => ({
    meta: [
      { title: "New Diplomats Initiative — Foreign Council On Economic Relations" },
      {
        name: "description",
        content:
          "International welcome receptions and induction seminars for newly accredited diplomats, fostering high-level cross-border dialogue and strategic networks.",
      },
      { property: "og:title", content: "New Diplomats Initiative — Foreign Council On Economic Relations" },
      {
        property: "og:description",
        content:
          "International welcome receptions and induction seminars for newly accredited diplomats, fostering high-level cross-border dialogue and strategic networks.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://foreign-council.org/new-diplomats" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://foreign-council.org/new-diplomats" }],
  }),
  component: NewDiplomatsPage,
});

function NewDiplomatsPage() {
  const { data: meetings } = useSuspenseQuery(meetingsQuery());
  const seminars = meetings.filter(
    (meeting) => meeting.category === "Induction Seminar" || meeting.category === "Welcome Reception",
  );

  const { isAuthenticated, isLoading } = useAuth0();
  const login = useMemberLogin();
  const { more } = Route.useSearch();
  const [showAll, setShowAll] = useState(more === 1);
  // Guests see the first GUEST_LIMIT events; the full gallery opens after sign-in.
  const base = isAuthenticated ? seminars : seminars.slice(0, GUEST_LIMIT);
  const rowCount = useCompleteRows(base.length, GRID_BREAKPOINTS);
  const visible = isAuthenticated && showAll ? base : base.slice(0, rowCount);
  const canLoadMore = isAuthenticated && !showAll && visible.length < base.length;

  return (
    <>
      <section className="bg-lilac">
        <div className="mx-auto grid w-full max-w-[1400px] items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-[90px]">
          <div>
            <p className="text-[24px] font-medium leading-tight text-foreground">Welcome!</p>
            <span className="mt-3 block h-px w-[22px] bg-foreground" aria-hidden />
            <h1 className="fc-h1 mt-8 text-foreground">
              Just arrived in Germany as a foreign diplomat?
            </h1>
            <p className="mt-10 text-[22px] italic leading-snug text-foreground sm:text-[26px]">
              Sign up for an invitation to our next induction seminar and welcoming reception for
              newly arrived diplomats!
            </p>
            <div className="mt-10 fc-body text-muted-foreground">
              <p className="font-bold text-foreground">Important!</p>
              <p>
                Registration is only possible for newly arrived diplomats with an official e-mail
                address (ministry, embassy, etc.). This seminar is in English, and participation
                is free of charge.
              </p>
            </div>
          </div>

          <form
            className="bg-highlight px-6 py-10 sm:px-11 sm:py-12"
            onSubmit={(event) => event.preventDefault()}
          >
            {FIELDS.map((field) => (
              <label key={field.label} className="mb-5 block">
                <span className="fc-body text-highlight-foreground">
                  {field.label}
                  {field.required && <span className="text-accent">*</span>}
                </span>
                <input
                  type={field.type}
                  required={field.required}
                  className="mt-1 h-10 w-full rounded-[3px] border border-border bg-secondary px-3 text-base text-foreground outline-none focus:border-accent"
                />
              </label>
            ))}
            <button
              type="submit"
              className="mt-6 bg-accent px-6 py-2.5 text-[20px] font-bold text-accent-foreground transition-opacity hover:opacity-85"
            >
              Submit
            </button>
          </form>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto w-full max-w-[1400px] px-5 py-16 lg:px-8 lg:py-20">
          <h2 className="fc-h2 fc-rule text-foreground">Our Induction Seminars</h2>

          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-[54px] font-extrabold leading-none tracking-[-1px] text-accent">
                  {stat.value}
                </p>
                <p className="mt-4 fc-body text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>

          <ul className="mt-16 grid gap-8 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-3 min-[1225px]:grid-cols-4">
            {visible.map((meeting) => {
              const parts = meetingDateParts(meeting);
              const location = meetingLocationLabel(meeting);
              const isUpcoming = meeting.is_upcoming || meeting.category === "Upcoming";
              return (
                <li key={meeting.id}>
                  <Link to="/meetings/$slug" params={{ slug: meeting.slug }} className="group block">

                    <div className="relative">
                      <img
                        src={meeting.cover_image_url ?? FALLBACK_IMAGE}
                        alt={meeting.title}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                      {parts && (
                        <div className="absolute left-0 top-0">
                          <DateBlock parts={parts} tone={isUpcoming ? "accent" : "light"} />
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-5 px-8 sm:px-10 lg:px-[45px]">
                        <div className="-ml-4 rounded-[6px_0_40px_0] bg-card p-3">
                          <p className="fc-meta text-accent">{location || "\u00A0"}</p>
                          <h3 className="fc-h4 mt-1 line-clamp-3 min-h-[78px] text-foreground group-hover:text-accent min-[640px]:min-h-[75px]">
                            {meeting.title}
                          </h3>
                        </div>
                      </div>

                    </div>
                  </Link>
                </li>
              );
            })}

          </ul>
          {canLoadMore && <LoadMoreButton onClick={() => setShowAll(true)} />}

          {!isLoading && !isAuthenticated && seminars.length > GUEST_LIMIT && (
            <div className="mt-16 flex flex-col items-center gap-6 border-t border-border px-2 py-12 text-center">
              <p className="fc-h4 text-foreground">
                Please sign in to see all seminars and receptions.
              </p>
              <button
                type="button"
                onClick={() => login("/new-diplomats?more=1")}
                className="bg-primary px-8 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85"
              >
                Login
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
